import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Box} from '@mui/material';
import {
    resetUpdatingStatus,
    retrieveEthnicity,
    retrieveEthnicityLookups,
    retrieveProfile,
    updateEthnicity
} from '../../ProfilesSlice';
import {showSnackbar} from '../../../../../AppLayout/ApplicationSlice';
import {useParams} from 'react-router-dom';
import Grid from '@mui/material/Grid';
import AdmicitySelect from '../../../../../shared-components/AdmicitySelect';
import Typography from '@mui/material/Typography';
import PassportFields from './PassportFields';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import PropTypes from 'prop-types';
import ConfirmationWrapper from '../../Components/ConfirmationWrapper';
import PROFILE_TYPE from '../../../../../constants/profileTypes';

const normaliseOptionTitle = (value = '') => String(value).trim().toLowerCase();

const prioritiseOptions = (options, priorityTitles = []) => {
    if (!priorityTitles.length) {
        return options;
    }

    const priorityTitleSet = new Set(priorityTitles.map(normaliseOptionTitle));
    const prioritised = [];
    const remaining = [];

    options.forEach(option => {
        if (priorityTitleSet.has(normaliseOptionTitle(option.title))) {
            prioritised.push(option);
        } else {
            remaining.push(option);
        }
    });

    return prioritised.length > 0
        ? [...prioritised, ...remaining]
        : options;
};

const buildSelectOptions = (list, priorityTitles = []) => {
    const options = list.map(element => ({
        key: element.code,
        value: element.code,
        title: element.description
    }));

    return prioritiseOptions(options, priorityTitles);
};

const hasValidSelectedOption = (value, options = []) => {
    if (value === null || value === undefined || value === '') {
        return false;
    }

    return options.some(option => String(option.value) === String(value));
};

const clearInvalidCulturalSelections = (ethnicity, columns) => {
    if (!ethnicity) {
        return {
            value: ethnicity,
            hasChanges: false
        };
    }

    let hasChanges = false;
    const sanitisedEthnicity = {...ethnicity};

    columns.forEach(column => {
        const currentValue = sanitisedEthnicity[column.key];
        const hasMeaningfulValue = currentValue !== null && currentValue !== undefined && currentValue !== '';

        if (hasMeaningfulValue && !hasValidSelectedOption(currentValue, column.options)) {
            sanitisedEthnicity[column.key] = null;
            hasChanges = true;
        }
    });

    return {
        value: hasChanges ? sanitisedEthnicity : ethnicity,
        hasChanges
    };
};

const buildAsylymStatusOptions = (list, passports) => {
    return passports?.some(p => p.nationCode === 'GBR')
        ? buildSelectOptions(list.filter(e => e.code === 'NOT_APPLICABLE'))
        : buildSelectOptions(list);
};

const isEngAdditionalLanguageOptions = [
    {
        code: 'false',
        description: 'No'
    },
    {
        code: 'true',
        description: 'Yes'
    },
];

const EthnicityFields = ({readonly}) => {
    const {
        selectedProfile,
        updatingSuccessful,
        ethnicityLookups,
        currentSchool
    } = useSelector(state => state.profilesInfo);
    const {studentId} = useParams();
    const [ethnicity, setEthnicity] = useState({});
    const [isEdited, setIsEdited] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const dispatch = useDispatch();
    const [passports, setPassports] = useState([]);
    const [deletedPassportIds, setDeletedPassportIds] = useState([]);
    const studentName = selectedProfile.name
        ? selectedProfile.name
        : '';

    useEffect(() => {
        dispatch(retrieveEthnicityLookups()).then(() => {
            dispatch(retrieveEthnicity({studentId}));
        });
    }, []);

    useEffect(() => {
        if (selectedProfile.ethnicityData) {
            setPassports(selectedProfile.ethnicityData.nationalityPassport);
            setEthnicity(selectedProfile.ethnicityData);
        }
    }, [selectedProfile.ethnicityData]);

    useEffect(() => {
        if (updatingSuccessful === undefined) {
            return;
        }

        const syncAfterUpdate = async () => {
            showNotification(updatingSuccessful);

            if (updatingSuccessful) {
                await Promise.all([
                    dispatch(retrieveEthnicity({studentId})),
                    dispatch(retrieveProfile({studentId}))
                ]);
            }

            setIsSaving(false);
            dispatch(resetUpdatingStatus());
        };

        syncAfterUpdate();
    }, [dispatch, studentId, updatingSuccessful]);

    const ethnicityOptions = useMemo(
        () => buildSelectOptions(ethnicityLookups?.ethnicities ?? []),
        [ethnicityLookups?.ethnicities]
    );

    const religionOptions = useMemo(
        () => buildSelectOptions(ethnicityLookups?.religions ?? []),
        [ethnicityLookups?.religions]
    );

    const languageOptions = useMemo(
        () => buildSelectOptions(ethnicityLookups?.languages ?? [], ['English']),
        [ethnicityLookups?.languages]
    );

    const englishAdditionalLanguageOptions = useMemo(
        () => buildSelectOptions(isEngAdditionalLanguageOptions),
        []
    );

    const asylumStatusOptions = useMemo(
        () => buildAsylymStatusOptions(ethnicityLookups?.asylymStatuses ?? [], passports),
        [ethnicityLookups?.asylymStatuses, passports]
    );

    const countryOfBirthOptions = useMemo(
        () => buildSelectOptions(ethnicityLookups?.nations ?? [], ['United Kingdom']),
        [ethnicityLookups?.nations]
    );

    const nationalityOptions = useMemo(
        () => buildSelectOptions(ethnicityLookups?.nationalIdentities ?? []),
        [ethnicityLookups?.nationalIdentities]
    );

    const columns = useMemo(() => [
        {
            description: `What is ${studentName}’s ethnicity?`,
            value: ethnicity.ethnicityCode,
            options: ethnicityOptions,
            key: 'ethnicityCode',
            required: true
        },
        {
            description: `What is ${studentName}’s religion?`,
            value: ethnicity.religionCode,
            options: religionOptions,
            key: 'religionCode',
            required: true
        },
        {
            description: `What was the first language ${studentName} was exposed to?`,
            value: ethnicity.firstLanguageCode,
            options: languageOptions,
            key: 'firstLanguageCode',
            required: true
        },
        {
            description: `What language does ${studentName} speak at home?`,
            value: ethnicity.homeLanguageCode,
            options: languageOptions,
            key: 'homeLanguageCode',
            required: true
        },
        {
            description: `Does ${studentName} speak English as an additional language?`,
            value: ethnicity.isEnglishAdditional,
            options: englishAdditionalLanguageOptions,
            key: 'isEnglishAdditional',
            required: true
        },
        {
            description: `Does ${studentName} have refugee or asylum status?`,
            value: ethnicity.asylymStatusCode || 'NOT_APPLICABLE',
            options: asylumStatusOptions,
            key: 'asylymStatusCode',
            required: true
        },
        {
            description: `What is ${studentName}’s country of birth?`,
            value: ethnicity.countryOfBirthCode,
            options: countryOfBirthOptions,
            key: 'countryOfBirthCode',
            required: true
        },
        {
            description: `What is ${studentName}’s nationality?`,
            value: ethnicity.nationalIdentityCode,
            options: nationalityOptions,
            key: 'nationalIdentityCode',
            required: true
        },
    ], [
        studentName,
        ethnicity.ethnicityCode,
        ethnicity.religionCode,
        ethnicity.firstLanguageCode,
        ethnicity.homeLanguageCode,
        ethnicity.isEnglishAdditional,
        ethnicity.asylymStatusCode,
        ethnicity.countryOfBirthCode,
        ethnicity.nationalIdentityCode,
        ethnicityOptions,
        religionOptions,
        languageOptions,
        englishAdditionalLanguageOptions,
        asylumStatusOptions,
        countryOfBirthOptions,
        nationalityOptions
    ]);

    useEffect(() => {
        if (!selectedProfile.ethnicityData || Object.keys(ethnicityLookups ?? {}).length === 0) {
            return;
        }

        setEthnicity(currentEthnicity => {
            const sanitisedEthnicity = clearInvalidCulturalSelections(currentEthnicity, columns);

            if (sanitisedEthnicity.hasChanges) {
                setIsEdited(true);
            }

            return sanitisedEthnicity.value;
        });
    }, [selectedProfile.ethnicityData, ethnicityLookups]);

    const showNotification = (success) => success !== undefined && dispatch({
        true: () => showSnackbar({
            message: 'Ethnicity updated successfully', severity: 'success'
        }),
        false: () => showSnackbar({
            message: 'Failed to update ethnicity', severity: 'error'
        })
    }[success]());

    const handleSave = async () => {
        const newPassports = passports.filter(e => e.isNew);
        const editedPassports = passports.filter(e => e.isEdited);

        setIsSaving(true);

        await dispatch(updateEthnicity({
            ethnicity: isEdited ? ethnicity : null,
            studentId,
            newPassports,
            editedPassports,
            deletedPassportIds
        })).unwrap();

        setIsEdited(false);
        setDeletedPassportIds([]);
    };

    const handleChangeValue = (value, key) => {
        let updatedEthnicity = JSON.parse(JSON.stringify(ethnicity));
        if (key !== 'isEnglishAdditional') {
            updatedEthnicity[key] = value ? value : null;
        } else {
            updatedEthnicity.isEnglishAdditional = value.toString();
        }
        setEthnicity(updatedEthnicity);
        setIsEdited(true);
    };

    const isAllEthnicityFieldsFilled = () =>
        columns
            .filter(column => column.required)
            .every(column => hasValidSelectedOption(column.value, column.options));

    const hasEditedOrNewPassports = passports?.some(e => e.isEdited || e.isNew);
    const isEthnicityConfirmed = !!selectedProfile.ethnicityData?.confirmedAt;
    const isAnyPassportDeleted = deletedPassportIds.length > 0;

    return (
        <ConfirmationWrapper
            readonly={readonly}
            hideActions={selectedProfile.personType === PROFILE_TYPE.ENQUIRY}
            confirmData={{
                confirmMessage: `I confirm that the information in the cultural profile section of ${selectedProfile.name} ${selectedProfile.surname} student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
                confirmedAt: selectedProfile.ethnicityData?.confirmedAt,
                confirmedBy: selectedProfile.ethnicityData?.confirmedBy,
                enableSaveButton: !isSaving && isAllEthnicityFieldsFilled() &&
                    (isEdited || hasEditedOrNewPassports || isAnyPassportDeleted),
                enableConfirmButton: !isSaving && !isEdited && !hasEditedOrNewPassports &&
                    !isAnyPassportDeleted && !isEthnicityConfirmed && isAllEthnicityFieldsFilled(),
                studentId,
                type: STUDENT_PROFILE_CONFIRMATION_TYPES.Cultural,
                confirmOnSave: true,
                handleSave,
                onConfirmSuccess: async () => {
                    await Promise.all([
                        dispatch(retrieveEthnicity({studentId})),
                        dispatch(retrieveProfile({studentId}))
                    ]);
                },
                confirmButtonText: 'Confirm Cultural Information'
            }}
        >
            <Typography variant="h6" marginTop={2} gutterBottom>CULTURAL INFORMATION REQUIRED BY THE DEPARTMENT FOR
                EDUCATION</Typography>
            <Typography variant="body1">All questions in this section must be responded to. If you do not wish to
                provide answers to any of these questions, please select ‘Refused’ from the list of options. Please
                note, before completing this section, that <strong>English as an Additional Language</strong> is defined by the
                Department for Education as <strong>‘Any pupil exposed to a language at home that is known or believed to be
                other than English’.</strong></Typography>
                <Grid container direction="row" justify="center" alignItems="center" spacing={4} py={3}>
                {columns && columns.map(column =>
                    <Grid item xs={12} sm={6}
                          key={column.key}>
                        <Box>
                            <Typography fontWeight="bold"
                                        variant="body1">{column.description} {!column.required ? '' : '*'}</Typography>
                            <AdmicitySelect
                                value={column.value ?? ''}
                                options={column.options}
                                widthValue={'100%'}
                                defaultValue={true}
                                onChange={event => handleChangeValue(event.target.value, column.key)}
                                disabled={readonly}
                            />
                        </Box>
                    </Grid>
                )}
            </Grid>
            <PassportFields
                readonly={readonly}
                passports={passports}
                setPassports={setPassports}
                countries={ethnicityLookups.nations}
                setDeletedPassportIds={setDeletedPassportIds}
                deletedPassportIds={deletedPassportIds}
                asylymStatus={ethnicity.asylymStatusCode}
                student={{
                    name: selectedProfile.name
                }}
            />
        </ConfirmationWrapper>
    );
};

EthnicityFields.propTypes = {
    readonly: PropTypes.bool
};

export default EthnicityFields;
